import type { CatalogEntry } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { Checkbox } from "./Checkbox";

function text(value: CatalogEntry["name"]) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.es ?? "";
}

// PA-025-D (agent-logs/runs/2026-09-09-spec-211-portal/evidence/
// human-resolution-pa-025.json): extraído desde rewards/RewardsScreen.tsx,
// donde vivía como función local por PA-023-C (SPEC-210). SPEC-211
// (Beneficios Star Circle) reutiliza la misma selección múltiple de lugares
// de consumo (comercios aliados y talleres, ANNEX-011) — duplicar estas
// ~60 líneas en dos pantallas haría fallar `dup:check` (`web/.jscpd.json`,
// threshold 3) sobre una deuda de duplicación que el humano ya dejó
// declarada (SPEC-210 known_defects).
//
// Primitiva mínima — una lista de checkboxes con el mismo borde/tipografía
// que el resto del formulario, sin inventar lenguaje visual (`RFieldGroup`
// ya provee la etiqueta y el hint). Extracción sin cambio de comportamiento
// ni de valores visuales: ninguna pantalla que la use cambia un pixel por
// esto.
export function PlaceCheckboxList({
  legend,
  entries,
  selected,
  onToggle,
  isLoading,
}: {
  legend: string;
  entries: CatalogEntry[] | undefined;
  selected: readonly string[];
  onToggle: (id: string) => void;
  isLoading: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={legend}
      // `portal-scroll`: barra fina del sistema (base.css) en el panel interno
      // de "Comercios aliados"/"Talleres" — revisión en vivo 2026-09-22, punto
      // #3. La clase aporta solo la estética; el `overflowY` de abajo, que es
      // el que hace que el panel desplace, no se mueve de acá.
      className="portal-scroll"
      style={{
        border: `1px solid ${tokens.surface.line2}`,
        borderRadius: 11,
        padding: "6px 12px",
        maxHeight: 132,
        overflowY: "auto",
        background: "rgba(255,255,255,0.03)",
      }}
    >
      {isLoading ? (
        <div
          style={{
            padding: "8px 0",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.disabled,
          }}
        >
          Cargando…
        </div>
      ) : !entries?.length ? (
        <div
          style={{
            padding: "8px 0",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.disabled,
          }}
        >
          Sin entradas activas en este catálogo.
        </div>
      ) : (
        entries.map((entry) => (
          // Revisión en vivo 2026-09-22, punto #4B: la fila deja de ser el
          // `<label>` (ahora lo es el `Checkbox`) y pasa a ser el `<div>` que
          // conserva el separador y el alto de la lista.
          <div
            key={entry.id}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 0",
              borderTop:
                entries[0]?.id === entry.id
                  ? "none"
                  : `1px solid ${tokens.surface.line}`,
            }}
          >
            <Checkbox
              checked={selected.includes(entry.id)}
              onChange={() => onToggle(entry.id)}
              label={text(entry.name)}
            />
          </div>
        ))
      )}
    </div>
  );
}
